/**
 * Merchant_wallet.jsx
 * ESDRAS MERCHANT MARKETPLACE — Merchant Wallet (Standalone Page)
 * Route: /merchant/wallet
 *
 * Features:
 *  • Real-time balance cards (available, pending, earned, withdrawn, net retained)
 *  • Withdrawal with amount validation + confirmation modal
 *  • Quick % shortcuts (25 / 50 / 75 / ALL)
 *  • Cursor-based paginated transaction history (50/page)
 *  • CSV export of up to 1,000 transactions
 *  • Bank details preview + quick link to update via dashboard
 *  • Auth guard — super admins redirect to /admin/merchant
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit,
  startAfter, getDocs, onSnapshot, doc, getDoc,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../firebase';
import { toast } from 'react-hot-toast';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const GOLD         = '#B8860B';
const DEEP_BLACK   = '#050a0f';
const CARD_BG      = '#0a0f14';
const CARD_BG2     = '#0d1520';
const BORDER       = '#1a2631';
const PAGE_SIZE    = 50;
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];

const TX_COLORS = {
  sale_credit:  { color: '#4caf50', bg: '#0a1f0a', icon: '📈', label: 'Sale Credit'   },
  withdrawal:   { color: '#ff4d4d', bg: '#1a0a0a', icon: '💸', label: 'Withdrawal'    },
  platform_fee: { color: '#f59e0b', bg: '#1a1200', icon: '📊', label: 'Platform Fee'  },
  delivery_fee: { color: '#60a5fa', bg: '#0a1020', icon: '🚚', label: 'Delivery Fee'  },
  refund:       { color: '#a855f7', bg: '#1a0a1a', icon: '↩️', label: 'Refund'        },
};

// ─── CURSOR-BASED PAGINATION HOOK ─────────────────────────────────────────────
function usePaginatedTransactions(merchantId) {
  const [rows,        setRows]        = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNext,     setHasNext]     = useState(false);
  const cursors = useRef([]);
  const [loading, setLoading] = useState(true);   // ← ADD THIS

  const load = useCallback(async (pageIdx, afterDoc) => {
    if (!merchantId) { setLoading(false); return; }
    setLoading(true);   // ← FIX: was setLoading(false) — must be true at START
    try {
      const base = query(
        collection(db, 'merchant_transactions'),
        where('merchantId', '==', merchantId),
        orderBy('createdAt', 'desc'),
      );
      const paged = afterDoc
        ? query(base, startAfter(afterDoc), limit(PAGE_SIZE + 1))
        : query(base, limit(PAGE_SIZE + 1));

      const snap  = await getDocs(paged);
      const more  = snap.docs.length > PAGE_SIZE;
      const slice = snap.docs.slice(0, PAGE_SIZE);

      setRows(slice.map(d => ({ id: d.id, ...d.data(), _snap: d })));
      setHasNext(more);
      setCurrentPage(pageIdx);
      if (slice.length > 0) cursors.current[pageIdx] = slice[slice.length - 1];
    } catch (err) {
      console.error('Wallet tx fetch:', err);
      toast.error('Could not load transactions.');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    if (merchantId) { cursors.current = []; load(0, null); }
  }, [merchantId, load]);

  const goFirst = () => { cursors.current = []; load(0, null); };
  const goNext  = () => { const c = cursors.current[currentPage]; if (c) load(currentPage + 1, c); };
  const goPrev  = () => {
    if (currentPage === 0) return;
    const c = currentPage >= 2 ? cursors.current[currentPage - 2] : null;
    load(currentPage - 1, c);
  };

  return { rows, loading, currentPage, hasNext, hasPrev: currentPage > 0, goFirst, goNext, goPrev };
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function Merchant_wallet() {
  const navigate  = useNavigate();
  const functions = getFunctions();

  const [user,        setUser]        = useState(null);
  const [merchant,    setMerchant]    = useState(null);
  const [wallet,      setWallet]      = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [withdrawAmt,  setWithdrawAmt]  = useState('');
  const [withdrawing,  setWithdrawing]  = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [exporting,    setExporting]    = useState(false);

 // ── Auth + Load Real Data (Super Admins get FULL real access) ─────────────────
useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (u) => {
    if (!u) {
      navigate('/');
      return;
    }

    setUser(u);

    // SUPER ADMIN = Real access (no dummy data)
    const isSuperAdmin = SUPER_ADMINS.includes(u.uid);

    try {
      const [mSnap, wSnap] = await Promise.all([
        getDoc(doc(db, 'merchants', u.uid)),
        getDoc(doc(db, 'merchant_wallets', u.uid)),
      ]);

      // For normal merchants: must be approved
      if (!isSuperAdmin) {
        if (!mSnap.exists() || mSnap.data().status !== 'approved') {
          toast.error('Merchant access denied.');
          navigate('/');
          return;
        }
      }

      // Super admins are always allowed (even if no merchant doc yet)
      setMerchant(mSnap.exists() 
        ? { id: mSnap.id, ...mSnap.data() } 
        : { 
            id: u.uid, 
            businessName: "Esdras Super Admin", 
            status: "approved",
            isSuperAdminView: true 
          }
      );

       setWallet(
          wSnap.exists()
            ? wSnap.data()
            : { availableBalance: 0, pendingBalance: 0, totalEarned: 0, totalWithdrawn: 0 }
        );

        setAuthLoading(false);  // ← FIX: was setLoading(false) — undefined
      } catch (err) {
        console.error('Error loading merchant wallet:', err);
        toast.error('Failed to load wallet data.');
        setAuthLoading(false);  // ← FIX: was missing entirely
        navigate('/');
      }
    });
    return () => unsub();
  }, [navigate]);

// ── Real-time wallet balance (works for BOTH super admins and merchants) ─────
useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, 'merchant_wallets', user.uid),
      snap => { if (snap.exists()) setWallet(snap.data()); },
      err  => console.error('Wallet listener error:', err),
    );
  }, [user]);

  const txPag = usePaginatedTransactions(user?.uid);

  // ── Computed values ───────────────────────────────────────────────────────
  const avail     = wallet?.availableBalance || 0;
  const pending   = wallet?.pendingBalance   || 0;
  const earned    = wallet?.totalEarned      || 0;
  const withdrawn = wallet?.totalWithdrawn   || 0;
  const netRetained = earned - withdrawn;

  // ── Withdrawal ────────────────────────────────────────────────────────────
  const handleWithdraw = () => {
    const amt = Number(withdrawAmt);
    if (!amt || amt < 100)     { toast.error('Minimum withdrawal is ₦100.');          return; }
    if (amt > avail)           { toast.error('Amount exceeds available balance.');     return; }
    if (!merchant?.bankCode)   { toast.error('Bank details not set. Update profile.'); return; }
    setShowConfirm(true);
  };

  const confirmWithdraw = async () => {
    setShowConfirm(false);
    setWithdrawing(true);
    try {
      const fn = httpsCallable(functions, 'merchantWithdraw');
      const r  = await fn({ amount: Number(withdrawAmt), merchantId: user.uid });
      if (r.data?.success) {
        toast.success(`₦${Number(withdrawAmt).toLocaleString()} withdrawal initiated!`);
        setWithdrawAmt('');
        txPag.goFirst();
      } else toast.error(r.data?.message || 'Withdrawal failed.');
    } catch (err) {
      toast.error(err.message || 'Withdrawal error. Please try again.');
    } finally {
      setWithdrawing(false);
    }
  };

  // ── CSV Export ────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'merchant_transactions'),
        where('merchantId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(1000),
      ));
      const all = snap.docs.map(d => d.data());
      const csvRows = [
        ['Date', 'Type', 'Description', 'Direction', 'Amount (NGN)', 'Balance After (NGN)', 'Order ID', 'Payment Reference'],
        ...all.map(tx => [
          tx.createdAt?.toDate?.().toLocaleDateString('en-NG') || '',
          tx.type || '',
          tx.description || '',
          tx.direction || '',
          tx.amount || 0,
          tx.balanceAfter || 0,
          tx.orderId || '',
          tx.paymentReference || '',
        ]),
      ];
      const csv  = csvRows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `EsdrasWallet_${merchant?.businessName?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${all.length} transactions.`);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  if (authLoading) return <LoadingScreen />;

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>

      {/* ==================== SUPER ADMIN BANNER ==================== */}
      {merchant?.isSuperAdminView && (
        <div style={{
          background: '#1a1200',
          color: GOLD,
          padding: '14px 20px',
          textAlign: 'center',
          fontWeight: '900',
          fontSize: '0.9rem',
          borderBottom: `2px solid ${GOLD}55`,
          letterSpacing: '1.5px'
        }}>
          SUPER ADMIN PREVIEW MODE — Merchant Wallet (Real Data • Full Functionality)
        </div>
      )}
      
      {/* Confirmation Modal */}
      {showConfirm && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <p style={{ fontSize: '2.5rem', textAlign: 'center', margin: '0 0 12px' }}>💸</p>
            <h3 style={{ color: GOLD, letterSpacing: '2px', margin: '0 0 14px', textAlign: 'center' }}>
              CONFIRM WITHDRAWAL
            </h3>
            <div style={S.confirmBox}>
              {[
                ['Amount',        `₦${Number(withdrawAmt).toLocaleString()}`,  GOLD,   '1.5rem'],
                ['To Bank',       merchant?.bankName || '—',                   '#aaa', null],
                ['Account No.',   merchant?.bankAccountNumber || '—',          '#aaa', null],
                ['Balance After', `₦${(avail - Number(withdrawAmt)).toLocaleString()}`, '#888', null],
              ].map(([label, val, color, size]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${BORDER}` }}>
                  <span style={{ color: '#555', fontSize: '0.72rem', fontWeight: '700' }}>{label}</span>
                  <span style={{ color, fontWeight: '900', fontSize: size || '0.88rem' }}>{val}</span>
                </div>
              ))}
            </div>
            <button onClick={confirmWithdraw} style={{ ...S.goldBtn, marginTop: '14px' }}>
              CONFIRM & WITHDRAW
            </button>
            <button onClick={() => setShowConfirm(false)} style={S.ghostBtn}>CANCEL</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <button onClick={() => navigate('/merchant/dashboard')} style={S.backBtn}>← DASHBOARD</button>
        <div>
          <h1 style={S.pageTitle}>MY WALLET</h1>
          <p style={S.pageSub}>{merchant?.businessName}</p>
        </div>
        <button onClick={handleExport} disabled={exporting} style={S.exportBtn}>
          {exporting ? 'EXPORTING…' : '⬇ EXPORT CSV'}
        </button>
      </div>

      <div style={S.container}>
        {/* Balance Cards */}
        <div style={S.balanceGrid}>
          {/* Main card — spans 2 columns */}
          <div style={{ ...S.balCard, gridColumn: 'span 2', border: `1px solid ${GOLD}55`, background: `${GOLD}08` }}>
            <p style={S.balLabel}>AVAILABLE BALANCE</p>
            <p style={{ color: GOLD, fontWeight: '900', fontSize: '3rem', margin: '4px 0 8px', lineHeight: 1 }}>
              ₦{avail.toLocaleString()}
            </p>
            <p style={{ color: '#444', fontSize: '0.72rem', margin: 0 }}>
              {merchant?.bankName ? `Withdrawable to ${merchant.bankName} — ${merchant.bankAccountNumber}` : '⚠️ Bank details not set — update your profile'}
            </p>
          </div>
          {[
            { label: 'PENDING CLEARANCE', value: pending,      color: '#f59e0b' },
            { label: 'TOTAL EARNED',       value: earned,      color: '#4caf50' },
            { label: 'TOTAL WITHDRAWN',    value: withdrawn,   color: '#888'    },
            { label: 'NET RETAINED',       value: netRetained, color: '#60a5fa' },
          ].map(c => (
            <div key={c.label} style={S.balCard}>
              <p style={S.balLabel}>{c.label}</p>
              <p style={{ color: c.color, fontWeight: '900', fontSize: '1.5rem', margin: '4px 0 0' }}>
                ₦{Number(c.value).toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* Withdrawal Form */}
        <div style={S.withdrawCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <p style={S.sectionTitle}>WITHDRAW EARNINGS</p>
              <p style={{ color: '#444', fontSize: '0.72rem', margin: '4px 0 0' }}>
                Monnify transfer · typically within 24 hours
              </p>
            </div>
            <button onClick={() => navigate('/merchant/dashboard')} style={S.linkBtn}>
              ✏️ Edit Bank Details
            </button>
          </div>

          {avail <= 0 ? (
            <div style={S.infoBox}>
              <p style={{ color: '#555', fontSize: '0.88rem', margin: 0 }}>
                No available balance yet. Earnings appear here once sales are settled.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input
                  type="number"
                  placeholder={`Amount (₦100 – ₦${avail.toLocaleString()})`}
                  value={withdrawAmt}
                  onChange={e => setWithdrawAmt(e.target.value)}
                  min="100" max={avail}
                  style={S.input}
                />
                {/* Quick select buttons */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                  {[['25%', 0.25], ['50%', 0.5], ['75%', 0.75], ['ALL', 1]].map(([lbl, f]) => (
                    <button key={lbl} onClick={() => setWithdrawAmt(String(Math.floor(avail * f)))}
                      style={S.quickBtn}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={S.bankTag}>
                  <span style={{ color: '#444', fontSize: '0.67rem' }}>To →</span>
                  <span style={{ color: '#aaa', fontSize: '0.78rem', fontWeight: '600' }}>
                    {merchant?.bankName || 'Bank not set'} — {merchant?.bankAccountNumber || '----'}
                  </span>
                </div>
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawing || !withdrawAmt || Number(withdrawAmt) < 100 || !merchant?.bankCode}
                  style={{
                    ...S.goldBtn,
                    width: 'auto', padding: '13px 24px',
                    opacity: (withdrawing || !withdrawAmt || Number(withdrawAmt) < 100 || !merchant?.bankCode) ? 0.5 : 1,
                  }}>
                  {withdrawing ? 'PROCESSING…' : 'WITHDRAW'}
                </button>
              </div>
            </div>
          )}

          {!merchant?.bankCode && (
            <p style={{ color: '#f59e0b', fontSize: '0.75rem', marginTop: '10px' }}>
              ⚠️ Please add your bank details in your <strong>Dashboard → Profile tab</strong> before making withdrawals.
            </p>
          )}
        </div>

        {/* Transaction History */}
        <div style={S.txCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <p style={S.sectionTitle}>TRANSACTION HISTORY</p>
              <p style={{ color: '#444', fontSize: '0.72rem', margin: '4px 0 0' }}>
                Page {txPag.currentPage + 1} · {txPag.rows.length} records · max {PAGE_SIZE}/page
              </p>
            </div>
          </div>

          {txPag.loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '30px 0' }}>
              <div style={S.spinner} />
              <p style={{ color: '#444', fontSize: '0.78rem', margin: 0 }}>Loading transactions…</p>
            </div>
          ) : txPag.rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', border: `1px dashed ${BORDER}`, borderRadius: '12px' }}>
              <p style={{ fontSize: '2rem', marginBottom: '10px' }}>💳</p>
              <p style={{ color: GOLD, fontWeight: '700', margin: '0 0 6px', fontSize: '0.88rem' }}>No transactions yet</p>
              <p style={{ color: '#555', fontSize: '0.82rem', margin: 0 }}>
                Your sales, fees, and withdrawals will appear here once you start making sales.
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      {['Date', 'Type', 'Description', 'Order ID', 'Amount', 'Balance After'].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {txPag.rows.map((tx, i) => {
                      const tc = TX_COLORS[tx.type] || { color: '#aaa', bg: '#1a1a1a', icon: '💰', label: tx.type };
                      return (
                        <tr key={tx.id} style={{ background: i % 2 ? '#060c1180' : 'transparent' }}>
                          <td style={S.td}>
                            {tx.createdAt?.toDate?.().toLocaleDateString('en-NG', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            }) || '—'}
                          </td>
                          <td style={S.td}>
                            <span style={{ background: tc.bg, color: tc.color, fontSize: '0.6rem', fontWeight: '900', padding: '3px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                              {tc.icon} {tc.label}
                            </span>
                          </td>
                          <td style={{ ...S.td, color: '#ccc', maxWidth: '240px' }}>
                            <span style={{ fontSize: '0.8rem', lineHeight: '1.4', display: 'block' }}>{tx.description}</span>
                          </td>
                          <td style={S.td}>
                            {tx.orderId
                              ? <span style={{ color: '#444', fontFamily: 'monospace', fontSize: '0.65rem' }}>{tx.orderId.substring(0, 14)}…</span>
                              : <span style={{ color: '#222' }}>—</span>}
                          </td>
                          <td style={{ ...S.td, textAlign: 'right' }}>
                            <span style={{ color: tx.direction === 'credit' ? '#4caf50' : '#ff4d4d', fontWeight: '900', fontSize: '0.9rem' }}>
                              {tx.direction === 'credit' ? '+' : '−'}₦{Number(tx.amount || 0).toLocaleString()}
                            </span>
                          </td>
                          <td style={{ ...S.td, textAlign: 'right', color: '#555', fontSize: '0.78rem' }}>
                            ₦{Number(tx.balanceAfter || 0).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderTop: `1px solid ${BORDER}`, flexWrap: 'wrap', gap: '10px', marginTop: '4px' }}>
                <p style={{ color: '#444', fontSize: '0.72rem', margin: 0 }}>
                  Page {txPag.currentPage + 1} · {txPag.rows.length} of {txPag.rows.length < PAGE_SIZE ? txPag.rows.length : `${PAGE_SIZE}+`} records
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {txPag.hasPrev && <button onClick={txPag.goFirst} style={pBtn}>« First</button>}
                  <button onClick={txPag.goPrev} disabled={!txPag.hasPrev} style={{ ...pBtn, opacity: txPag.hasPrev ? 1 : 0.3 }}>‹ Prev</button>
                  <span style={{ background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}44`, padding: '6px 14px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '900' }}>
                    {txPag.currentPage + 1}
                  </span>
                  <button onClick={txPag.goNext} disabled={!txPag.hasNext} style={{ ...pBtn, opacity: txPag.hasNext ? 1 : 0.3 }}>Next ›</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── SHARED ───────────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: DEEP_BLACK, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}22`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
        <p style={{ color: GOLD, letterSpacing: '3px', marginTop: '16px', fontSize: '0.75rem' }}>LOADING WALLET…</p>
      </div>
      <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const pBtn = { background: CARD_BG, border: `1px solid ${BORDER}`, color: '#888', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '700', fontFamily: 'inherit' };

const S = {
  page:         { minHeight: '100vh', background: DEEP_BLACK, color: 'white', fontFamily: "'Montserrat','Inter',sans-serif" },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 32px', borderBottom: `1px solid ${BORDER}`, background: '#060c11', flexWrap: 'wrap', gap: '12px' },
  backBtn:      { background: 'transparent', border: `1px solid ${BORDER}`, color: '#555', padding: '9px 16px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'inherit', letterSpacing: '1px' },
  pageTitle:    { color: GOLD, fontWeight: '900', letterSpacing: '3px', margin: 0, fontSize: '1.3rem' },
  pageSub:      { color: '#444', fontSize: '0.72rem', margin: '3px 0 0', letterSpacing: '1px' },
  exportBtn:    { background: 'transparent', border: `1px solid ${GOLD}44`, color: GOLD, padding: '9px 18px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'inherit', fontWeight: '700', letterSpacing: '1px' },
  container:    { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' },
  balanceGrid:  { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' },
  balCard:      { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '16px', padding: '20px' },
  balLabel:     { color: '#444', fontSize: '0.6rem', letterSpacing: '2px', margin: '0 0 4px', fontWeight: '700' },
  withdrawCard: { background: CARD_BG, border: `1px solid ${GOLD}22`, borderRadius: '16px', padding: '24px', marginBottom: '20px' },
  sectionTitle: { color: GOLD, fontWeight: '900', fontSize: '0.72rem', letterSpacing: '2.5px', margin: 0 },
  linkBtn:      { background: 'transparent', border: 'none', color: GOLD, cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 },
  infoBox:      { background: CARD_BG2, border: `1px dashed ${BORDER}`, borderRadius: '12px', padding: '20px', textAlign: 'center' },
  quickBtn:     { background: CARD_BG2, border: `1px solid ${BORDER}`, color: '#666', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.68rem', fontFamily: 'inherit' },
  bankTag:      { background: CARD_BG2, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '10px 14px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  txCard:       { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '16px', padding: '24px' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: '700px' },
  th:           { color: GOLD, fontSize: '0.6rem', letterSpacing: '1.5px', fontWeight: '900', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${BORDER}`, background: CARD_BG2, whiteSpace: 'nowrap' },
  td:           { color: '#888', padding: '11px 12px', borderBottom: `1px solid #0d152055`, fontSize: '0.78rem', verticalAlign: 'middle' },
  input:        { background: CARD_BG2, border: `1px solid ${BORDER}`, padding: '13px 15px', borderRadius: '10px', color: 'white', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal:        { background: DEEP_BLACK, border: `1px solid ${GOLD}33`, borderRadius: '20px', padding: '32px 28px', width: '100%', maxWidth: '380px', display: 'flex', flexDirection: 'column', gap: '0' },
  confirmBox:   { background: CARD_BG2, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '4px 16px', marginBottom: '4px' },
  goldBtn:      { background: GOLD, color: 'black', padding: '15px', borderRadius: '50px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.88rem', letterSpacing: '1px', width: '100%', fontFamily: 'inherit' },
  ghostBtn:     { background: 'transparent', color: '#555', padding: '12px', borderRadius: '50px', fontWeight: '600', border: `1px solid ${BORDER}`, cursor: 'pointer', fontSize: '0.82rem', width: '100%', fontFamily: 'inherit', marginTop: '8px' },
  spinner:      { width: '24px', height: '24px', border: `2px solid ${GOLD}22`, borderTop: `2px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 },
};
